import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Leaf, ArrowLeft, Mail, Key, Search, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BUSINESS_NAME } from '@/branding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('choose'); // 'choose', 'forgot-password', 'find-email', 'reset-password'
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [foundAccounts, setFoundAccounts] = useState([]);
  const [revealedEmails, setRevealedEmails] = useState({});
  const [success, setSuccess] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Check for token in URL on mount (from email link)
  useEffect(() => {
    const emailFromState = location.state?.email;
    
    if (searchParams.has('token')) {
      const tokenFromUrl = searchParams.get('token') || '';
      setResetToken(tokenFromUrl);
      setMode('reset-password');
      if (tokenFromUrl) toast.info('Enter your new password below');
    }
    
    if (emailFromState) {
      setEmail(emailFromState);
      setMode('forgot-password');
    }
  }, [searchParams, location.state]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      // One generic outcome in every case - the page never reveals whether an account exists
      setEmailSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const handleFindEmail = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Please enter both first and last name');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/api/auth/find-email-by-name`, { 
        first_name: firstName.trim(),
        last_name: lastName.trim()
      });
      
      if (response.data.found) {
        setFoundAccounts(response.data.accounts);
        setRevealedEmails({});
        toast.success(`Found ${response.data.count} matching account(s)!`);
      } else {
        setFoundAccounts([]);
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to search');
    } finally {
      setLoading(false);
    }
  };

  const handleRevealEmail = (index) => {
    setRevealedEmails(prev => ({
      ...prev,
      [index]: true
    }));
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetToken) {
      toast.error('This reset link is invalid. Please request a new one.');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
      if (error.response?.status === 400) {
        // Invalid/expired/used token - show the clear error state instead of the form
        setResetToken('');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-primary/5 to-transparent">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Password Reset Complete!
          </h2>
          <p className="text-muted-foreground mb-6">
            Your password has been reset successfully. You can now login with your new password.
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="rounded-full px-8 bg-gradient-to-r from-primary to-[#8FD14F]"
          >
            Go to Login
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-primary/5 to-transparent">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/login" className="inline-flex items-center gap-2 mb-6 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>
          <div className="flex justify-center items-center gap-2 mb-4">
            <Leaf className="w-10 h-10 text-primary" />
            <span className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{BUSINESS_NAME}</span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Account Recovery
          </h1>
          <p className="text-muted-foreground">
            {mode === 'choose' && "Can't access your account? We'll help you."}
            {mode === 'forgot-password' && "Enter your email to reset your password"}
            {mode === 'find-email' && "Search for your account by partial email"}
            {mode === 'reset-password' && "Enter your new password"}
          </p>
        </div>

        <div className="bg-card text-card-foreground rounded-2xl border border-border/50 shadow-lg p-8">
          
          {mode === 'choose' && (
            <div className="space-y-4">
              <Button
                onClick={() => setMode('forgot-password')}
                variant="outline"
                className="w-full h-auto py-4 justify-start gap-4 rounded-xl"
                data-testid="forgot-password-option"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Key className="w-6 h-6 text-primary" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">I forgot my password</p>
                  <p className="text-sm text-muted-foreground">Reset password with email</p>
                </div>
              </Button>
              
              <Button
                onClick={() => setMode('find-email')}
                variant="outline"
                className="w-full h-auto py-4 justify-start gap-4 rounded-xl"
                data-testid="find-email-option"
              >
                <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                  <Search className="w-6 h-6 text-secondary" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">I forgot my email</p>
                  <p className="text-sm text-muted-foreground">Search by partial email</p>
                </div>
              </Button>
            </div>
          )}

          {mode === 'forgot-password' && (
            <>
              {emailSent ? (
                <div className="text-center space-y-6">
                  <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                    <Mail className="w-10 h-10 text-green-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Check Your Email</h3>
                    <p className="text-muted-foreground" data-testid="reset-generic-message">
                      If an account exists for this email address, we've sent a password reset link. Please check your inbox and spam folder. If nothing arrives within 15 minutes, contact <a href="mailto:support@insulnutrients.com" className="underline">support@insulnutrients.com</a>.
                    </p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left">
                    <p className="text-sm text-blue-800">
                      <strong>Next steps:</strong>
                    </p>
                    <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                      <li>Check your inbox (and spam folder)</li>
                      <li>Click the "Reset My Password" button in the email</li>
                      <li>Create your new password</li>
                    </ol>
                  </div>
                  <div className="space-y-3">
                    <Button
                      onClick={() => { setEmailSent(false); setEmail(''); }}
                      variant="outline"
                      className="w-full rounded-full"
                    >
                      Try a different email
                    </Button>
                    <Button
                      onClick={() => navigate('/login')}
                      className="w-full rounded-full bg-gradient-to-r from-primary to-[#8FD14F]"
                    >
                      Back to Login
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="rounded-xl"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-full py-6 bg-gradient-to-r from-primary to-[#8FD14F]"
                    data-testid="send-reset-button"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Link'}
                  </Button>
                  
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => setMode('choose')}
                  >
                    Back to options
                  </Button>
                </form>
              )}
            </>
          )}

          {mode === 'find-email' && (
            <form onSubmit={handleFindEmail} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                    First Name
                  </Label>
                  <Input
                    placeholder="Enter your first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="rounded-xl"
                    data-testid="find-email-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                    Last Name
                  </Label>
                  <Input
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="rounded-xl"
                    data-testid="find-email-last-name"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Enter your first and last name to find the email address associated with your account
                </p>
              </div>
              
              <Button
                type="submit"
                disabled={loading || !firstName.trim() || !lastName.trim()}
                className="w-full rounded-full py-6 bg-gradient-to-r from-secondary to-[#FF9B50]"
                data-testid="search-email-button"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Search'}
              </Button>
              
              {foundAccounts.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="text-sm font-semibold text-green-800 mb-3">Found account(s):</p>
                  <ul className="space-y-2">
                    {foundAccounts.map((account, idx) => (
                      <li key={`account-${account.masked_email}-${idx}`} className="bg-white rounded-lg p-3 border border-green-100">
                        {revealedEmails[idx] ? (
                          <div className="space-y-2">
                            <p className="text-sm font-mono text-green-700">{account.full_email}</p>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="w-full text-xs"
                              onClick={() => {
                                setEmail(account.full_email);
                                setMode('forgot-password');
                                setFoundAccounts([]);
                              }}
                            >
                              Use this email to reset password
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-mono text-green-700">{account.masked_email}</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="text-xs"
                              onClick={() => handleRevealEmail(idx)}
                              data-testid={`reveal-email-${idx}`}
                            >
                              Reveal
                            </Button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => { setMode('choose'); setFoundAccounts([]); setFirstName(''); setLastName(''); }}
              >
                Back to options
              </Button>
            </form>
          )}

          {mode === 'reset-password' && !resetToken && (
            <div className="text-center space-y-6" data-testid="reset-link-invalid-state">
              <div className="w-20 h-20 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-red-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Invalid or Expired Reset Link</h3>
                <p className="text-muted-foreground text-sm">
                  This page can only be opened from a valid password reset link. Your link may have
                  expired, or the address was entered incorrectly. Please request a new reset link below.
                </p>
              </div>
              <div className="space-y-3">
                <Button
                  onClick={() => setMode('forgot-password')}
                  className="w-full rounded-full py-6 bg-gradient-to-r from-primary to-[#8FD14F]"
                  data-testid="request-new-link-button"
                >
                  Request a New Reset Link
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => navigate('/login')}
                >
                  Back to Login
                </Button>
              </div>
            </div>
          )}

          {mode === 'reset-password' && resetToken && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  New Password
                </Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="rounded-xl"
                  data-testid="new-password-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  Confirm Password
                </Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="rounded-xl"
                  data-testid="confirm-password-input"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-full py-6 bg-gradient-to-r from-primary to-[#8FD14F]"
                data-testid="reset-password-button"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
              </Button>
              
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => { setMode('choose'); setResetToken(''); }}
              >
                Back to options
              </Button>
            </form>
          )}
        </div>

        {/* Account Locked Warning */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-800">
                <strong>Account locked?</strong> After 3 failed login attempts, accounts are locked for 24 hours. 
                You can still reset your password during this time.
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
